import { Component, HostBinding, Input } from '@angular/core';

import { WeatherService } from '../../weather/weather/weather.service';
import { LatLon } from '../../util/geodesy/latlon';
import { PromiseState, StatefulPromise } from '../../util/stateful-promise';
import { Taf } from '../../weather/models/taf.model';
import { WeatherLegendComponent } from '../../shared/weather-legend/weather-legend.component';
import { MatDialog } from '@angular/material';
import { Forecast } from '../../weather/models/forecast.model';


@Component({
  selector: 'app-taf-section',
  templateUrl: './taf-section.component.html',
  styleUrls: ['./taf-section.component.scss']
})
export class TafSectionComponent {
  private _latlon: LatLon;
  tafPromise: StatefulPromise<Taf[]>;

  @HostBinding('class.wx-section') _clsWxSection = true;

  /**
   * Sets the initial state of the Translated toggle in the header.
   */
  @Input() translated: boolean;

  /**
   * Sets the airport identifier. The TAF whose station identifier matches this identifier will be shown in the
   * top section of this component.
   */
  @Input() identifier: string;

  /**
   * Returns the latlon of the primary station identifier
   */
  get latlon(): LatLon {
    return this._latlon;
  }

  /**
   * Sets the lat/lon to be used as the center point when querying TAFs. This is required (all TAF data, including
   * for the primary identifier, is loaded by lat/lon).
   */
  @Input() set latlon(value: LatLon) {
    if (this._latlon !== value) {
      this._latlon = value;
      this._load();
    }
  }

  constructor(
    private _wx: WeatherService,
    public _dialog: MatDialog
  ) { }

  private _load() {
    this.tafPromise = new StatefulPromise(this._wx.getTafsByLatLon(this._latlon));
  }

  private get _tafs(): Array<Taf> {
    if (!this.tafPromise || this.tafPromise.state !== PromiseState.Fulfilled) {
      return null;
    }
    this.tafPromise.value.forEach((taf) => {
      this.fixADDSTempoIssue(taf);
    });
    return this.tafPromise.value;
  }

  /**
   * Returns the primary TAF
   * @returns {Taf}
   */
  get primaryTaf(): Taf {
    // this selects the TAF for the current airport, if there is one
    // it intentionally checks again after prepending a K to the identifier to account for fake ICAO ADDS airports (like M91)
    let tempTaf: Taf = null;
    if (this._tafs) {
      tempTaf = this._tafs.find(taf => taf.stationId === this.identifier);
      if (!tempTaf) {
        tempTaf = this._tafs.find(taf => taf.stationId === 'K' + this.identifier);
      }
    }
    return tempTaf;
  }

  /**
   * Returns nearby TAFs (excluding the primary TAF)
   * @returns {Array<Taf>}
   */
  get nearbyTafs(): Array<Taf> {
    // this returns the nearby TAFs by filtering out a local TAF, if there is one
    // it intentionally checks twice prepending a K to the identifier the second time to account for fake ICAO ADDS airports (like M91)
    return (this._tafs || []).filter(taf => taf.stationId !== this.identifier && taf.stationId !== 'K' + this.identifier);
  }

  openWeatherLegend() {
    const dialogRef = this._dialog.open(WeatherLegendComponent, {
      // height: '500px'
    });
  }

  fixADDSTempoIssue(taf: Taf) {
    const validflightCategories = new Set(['VFR', 'MVFR', 'IFR', 'LIFR']);
    let prevForecast: Forecast = taf.forecasts[0];
    taf.forecasts.forEach((forecast) => {
      if (forecast.changeIndicator === 'TEMPO') {
        let curCeiling = 9999;
        let curVisibility = 10;
        if (forecast.ceiling && forecast.ceiling !== 'None') {
          curCeiling = Number(forecast.ceiling.match(/\d+/)[0]);
        }
        if (forecast.visibility) {
          curVisibility = Number(forecast.visibility.match(/\d+/)[0]);
        }
        if (!validflightCategories.has(forecast.flightCategory)) {
          forecast.flightCategory = prevForecast.flightCategory;
          if (curCeiling <= 500 || curVisibility <= 1) {
            forecast.flightCategory = 'LIFR';
          } else if (curCeiling <= 1000 || curVisibility <= 3) {
            forecast.flightCategory = 'IFR';
          } else if (curCeiling <= 3000 || curVisibility <= 5) {
            forecast.flightCategory = 'MVFR';
          }
          // console.log('changed tempo: ', taf.stationId);
        }
        if (!forecast.visibility) { forecast.visibility = prevForecast.visibility; }
        if (!forecast.wxStringTrans) { forecast.wxStringTrans = prevForecast.wxStringTrans; }
        if (!forecast.wind || forecast.wind.calm) { forecast.wind = prevForecast.wind; }
        if (prevForecast.windShearHgtFtAgl && !forecast.windShearHgtFtAgl) {
          forecast.windShearHgtFtAgl = prevForecast.windShearHgtFtAgl;
        }
        if (prevForecast.windShearSpeedKt && !forecast.windShearSpeedKt) {
          forecast.windShearSpeedKt = prevForecast.windShearSpeedKt;
        }
        if (prevForecast.windShearDirDegrees && !forecast.windShearDirDegrees) {
          forecast.windShearDirDegrees = prevForecast.windShearDirDegrees;
        }
        if (forecast.turbulence.length === 0) { forecast.turbulence = prevForecast.turbulence; }
        if (forecast.icing.length === 0) { forecast.icing = prevForecast.icing; }
        if (forecast.skyConditions.length === 0) { forecast.skyConditions = prevForecast.skyConditions; }
      } else if (forecast.changeIndicator === 'FM' || forecast.changeIndicator === 'BECMG') {
        prevForecast = forecast;
      }
    });
  }
}
